import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import React from 'react';

/**
 * Image management utilities for admin components
 * Handles upload, cleanup, and memory management
 */

export interface ImageUploadOptions {
  bucket: string;
  folder?: string;
  maxSize?: number; // in bytes
  allowedTypes?: string[];
}

export interface ImageUploadResult {
  url: string;
  path: string;
}

/**
 * Upload image to Supabase Storage with validation and cleanup
 */
export const uploadImage = async (
  file: File,
  options: ImageUploadOptions,
  oldImageUrl?: string
): Promise<ImageUploadResult> => {
  const {
    bucket,
    folder = '',
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  } = options;

  // Validate file type
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Tipo de ficheiro não suportado. Use: ${allowedTypes.map(t => t.split('/')[1]).join(', ')}`);
  }

  // Validate file size
  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    throw new Error(`Ficheiro muito grande. Limite máximo: ${maxSizeMB}MB`);
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  if (!fileExt) {
    throw new Error('Ficheiro deve ter uma extensão válida');
  }

  const fileName = `${uuidv4()}.${fileExt}`;
  const filePath = folder ? `${folder}/${fileName}` : fileName;

  try {
    // Delete old image first if exists
    if (oldImageUrl) {
      await deleteImageFromUrl(oldImageUrl, bucket);
    }

    // Upload new image
    const { error: uploadError, data } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return {
      url: urlData.publicUrl,
      path: filePath
    };
  } catch (error) {
    console.error('Image upload error:', error);
    throw new Error(`Erro no upload: ${error.message}`);
  }
};

/**
 * Delete image from storage using URL
 */
export const deleteImageFromUrl = async (imageUrl: string, bucket: string): Promise<void> => {
  try {
    // Extract path from URL
    const urlParts = imageUrl.split('/');
    const bucketIndex = urlParts.findIndex(part => part === bucket);

    if (bucketIndex === -1) {
      console.warn('Could not extract path from image URL:', imageUrl);
      return;
    }

    const path = urlParts.slice(bucketIndex + 1).join('/');

    if (!path) {
      console.warn('Empty path extracted from URL:', imageUrl);
      return;
    }

    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) {
      console.warn('Error deleting image:', error);
      // Don't throw error for cleanup operations
    } else {
      console.log('Successfully deleted image:', path);
    }
  } catch (error) {
    console.warn('Error in deleteImageFromUrl:', error);
    // Don't throw error for cleanup operations
  }
};

/**
 * Delete image from storage using path
 */
export const deleteImageFromPath = async (path: string, bucket: string): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) {
      console.warn('Error deleting image by path:', error);
    } else {
      console.log('Successfully deleted image:', path);
    }
  } catch (error) {
    console.warn('Error in deleteImageFromPath:', error);
  }
};

/**
 * Cleanup multiple images
 */
export const cleanupImages = async (imageUrls: string[], bucket: string): Promise<void> => {
  const cleanupPromises = imageUrls.map(url => deleteImageFromUrl(url, bucket));
  await Promise.allSettled(cleanupPromises);
};

/**
 * Memory management utilities
 */
export const createObjectURL = (file: File): string => {
  return URL.createObjectURL(file);
};

export const revokeObjectURL = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * React hook for managing object URLs and cleanup
 */
export const useImagePreview = () => {
  const [previewUrls, setPreviewUrls] = React.useState<string[]>([]);

  const addPreview = React.useCallback((file: File): string => {
    const url = createObjectURL(file);
    setPreviewUrls(prev => [...prev, url]);
    return url;
  }, []);

  const removePreview = React.useCallback((url: string) => {
    revokeObjectURL(url);
    setPreviewUrls(prev => prev.filter(u => u !== url));
  }, []);

  const clearPreviews = React.useCallback(() => {
    previewUrls.forEach(revokeObjectURL);
    setPreviewUrls([]);
  }, [previewUrls]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      previewUrls.forEach(revokeObjectURL);
    };
  }, []);

  return {
    addPreview,
    removePreview,
    clearPreviews,
    previewUrls
  };
};

